class ProductForm {
  constructor(container) {
    this.container = container;
    this.form = container.querySelector('.product-form__form');
    this.productData = window.productData || {};
    this.selectedVariantId = this.productData.selectedVariantId;
    this.selectedVariant = this.findVariant(this.selectedVariantId);
    this.isSubscription = false;
    this.subscriptionDiscount = 0.15; // 15% discount

    // Libellés traduisibles fournis par le Liquid (data-attributes) -> gérés par Reversia
    const d = container.dataset;
    this.i18n = {
      addToCart: d.i18nAddToCart || 'Ajouter au panier',
      adding: d.i18nAdding || 'Ajout en cours...',
      added: d.i18nAdded || 'Produit ajouté !',
      errorRetry: d.i18nErrorRetry || 'Erreur, réessayer',
      errorTitle: d.i18nErrorTitle || 'Oups, une erreur est survenue',
      errorGeneric: d.i18nErrorGeneric || "Impossible d'ajouter ce produit au panier. Veuillez réessayer.",
      connectionErrorTitle: d.i18nConnectionErrorTitle || 'Erreur de connexion',
      connectionErrorBody: d.i18nConnectionErrorBody || "Impossible d'ajouter le produit au panier. Vérifiez votre connexion internet.",
      perUnit: d.i18nPerUnit || '/sachet',
    };

    this.init();
  }

  init() {
    // Debug: log initial state
    console.log('ProductForm initialized:', {
      productData: this.productData,
      selectedVariantId: this.selectedVariantId,
      selectedVariant: this.selectedVariant
    });

    this.initPackSelection();
    this.initSubscriptionToggle();
    this.initAddToCart();
    this.initShippingSlider();
    this.initVariantsSlider();
    this.initDeliveryDate();
    this.initReviewsScroll();

    // Only update prices if we have a valid variant
    if (this.selectedVariant) {
      this.updatePrices();
      this.updatePackQuantity();
    } else {
      console.error('No selected variant found on initialization');
    }
  }

  initDeliveryDate() {
    const deliveryDateElement = this.container.querySelector('[data-delivery-date]');
    const deliveryDaysContainer = this.container.querySelector('[data-delivery-days]');

    if (deliveryDateElement && deliveryDaysContainer) {
      const businessDays = parseInt(deliveryDaysContainer.dataset.deliveryDays);
      const deliveryDate = this.calculateDeliveryDate(businessDays);
      deliveryDateElement.textContent = this.formatDeliveryDate(deliveryDate);
    }
  }

  calculateDeliveryDate(businessDays) {
    const today = new Date();
    let addedDays = 0;
    let currentDate = new Date(today);

    while (addedDays < businessDays) {
      currentDate.setDate(currentDate.getDate() + 1);

      // Check if it's a business day (Monday=1 to Friday=5)
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        addedDays++;
      }
    }

    return currentDate;
  }

  formatDeliveryDate(date) {
    // Localise la date selon la langue de la page (traduction gérée par Shopify/Reversia)
    const locale = document.documentElement.lang || 'fr';
    try {
      return date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch (e) {
      return date.toLocaleDateString('fr', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    }
  }

  initReviewsScroll() {
    const reviewsLink = this.container.querySelector('.redirect-jdgm');

    if (reviewsLink) {
      reviewsLink.addEventListener('click', (e) => {
        e.preventDefault();

        const targetId = reviewsLink.getAttribute('href').substring(1);
        const targetElement = document.getElementById(targetId);

        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    }
  }

  initShippingSlider() {
    const shippingSwiper = this.container.querySelector('.product-form__shipping-swiper');

    if (shippingSwiper && typeof Swiper !== 'undefined') {
      new Swiper(shippingSwiper, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: true,
        autoplay: {
          delay: 3000,
          disableOnInteraction: false,
        },
        pagination: {
          el: '.product-form__shipping-pagination',
          clickable: true,
        },
      });
    }
  }

  initVariantsSlider() {
    const variantsSwiper = this.container.querySelector('.product-form__variants-swiper');

    if (variantsSwiper && typeof Swiper !== 'undefined') {
      // colonnes / rangées desktop réglables depuis l'éditeur de thème
      const desktopColumns = parseInt(variantsSwiper.dataset.desktopColumns) || 5;
      const desktopRows = parseInt(variantsSwiper.dataset.desktopRows) || 2;

      const prevButton = this.container.querySelector('.product-form__variants-nav--prev');
      const nextButton = this.container.querySelector('.product-form__variants-nav--next');

      new Swiper(variantsSwiper, {
        slidesPerView: 'auto',
        spaceBetween: 8,
        enabled: true,
        navigation: {
          prevEl: prevButton,
          nextEl: nextButton,
        },
        pagination: {
          el: '.product-form__variants-pagination',
          clickable: true,
        },
        breakpoints: {
          0: {
            slidesPerView: 'auto',
            spaceBetween: 8,
            enabled: true,
            grid: { rows: 1 },
          },
          990: {
            slidesPerView: desktopColumns,
            spaceBetween: 12,
            enabled: true,
            grid: { rows: desktopRows, fill: 'column' },
          }
        },
        on: {
          init: (sw) => this.syncVariantsControls(sw),
          resize: (sw) => this.syncVariantsControls(sw),
          paginationUpdate: (sw) => this.syncVariantsControls(sw),
          breakpoint: (sw) => this.syncVariantsControls(sw),
        },
      });
    }
  }

  syncVariantsControls(swiper) {
    const block = this.container.querySelector('.product-form__variants');
    if (!block) return;

    const pagination = block.querySelector('.product-form__variants-pagination');
    if (pagination) {
      block.style.setProperty('--variants-pagination-w', `${Math.round(pagination.offsetWidth)}px`);
    }

    // The Grid module needs an explicit height on the container to split the rows.
    // Derive it from the tallest label so a two-line product name never clips.
    const labels = block.querySelectorAll('.product-form__variant-label');
    if (labels.length) {
      let rowHeight = 0;
      labels.forEach((label) => {
        rowHeight = Math.max(rowHeight, label.offsetHeight);
      });
      if (rowHeight > 0) {
        block.style.setProperty('--variants-row-h', `${Math.ceil(rowHeight)}px`);
      }
      const sw = block.querySelector('.product-form__variants-swiper');
      if (sw) {
        block.style.setProperty('--variants-rows', parseInt(sw.dataset.desktopRows) || 2);
      }
    }
  }

  findVariant(variantId) {
    return this.productData.variants?.find(v => v.id === variantId);
  }

  initPackSelection() {
    const packOptions = this.container.querySelectorAll('.product-form__pack-option');

    packOptions.forEach(option => {
      const radio = option.querySelector('input[type="radio"]');
      if (radio) {
        radio.addEventListener('change', (e) => {
          // Remove selected class from all options
          packOptions.forEach(opt => opt.classList.remove('selected'));

          // Add selected class to clicked option
          option.classList.add('selected');

          // Update selected variant
          this.selectedVariantId = parseInt(e.target.value);
          this.selectedVariant = this.findVariant(this.selectedVariantId);

          // Update hidden input
          const variantInput = this.form.querySelector('[data-variant-id-input]');
          if (variantInput) {
            variantInput.value = this.selectedVariantId;
          }

          // Update prices
          this.updatePrices();

          // Update pack quantity
          this.updatePackQuantity();
        });
      }

      // Also handle click on the whole option
      option.addEventListener('click', (e) => {
        if (e.target.tagName !== 'INPUT') {
          const radio = option.querySelector('input[type="radio"]');
          if (radio) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change'));
          }
        }
      });
    });
  }

  initSubscriptionToggle() {
    const subscriptionOptions = this.container.querySelectorAll('.product-form__subscription-option');

    subscriptionOptions.forEach(option => {
      const radio = option.querySelector('input[type="radio"]');
      if (radio) {
        radio.addEventListener('change', (e) => {
          // Remove selected and checked classes
          subscriptionOptions.forEach(opt => {
            opt.classList.remove('selected');
            const checkbox = opt.querySelector('.product-form__subscription-checkbox');
            if (checkbox) {
              checkbox.classList.remove('checked');
            }
          });

          // Add selected class to clicked option
          option.classList.add('selected');
          const checkbox = option.querySelector('.product-form__subscription-checkbox');
          if (checkbox) {
            checkbox.classList.add('checked');
          }

          // Update subscription status
          this.isSubscription = e.target.value === 'subscription';

          // Update prices
          this.updatePrices();
        });
      }

      // Also handle click on the whole option
      option.addEventListener('click', (e) => {
        if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'SELECT') {
          const radio = option.querySelector('input[type="radio"]');
          if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change'));
          }
        }
      });
    });
  }

  calculatePricePerUnit(variantPrice, quantity) {
    // Extract quantity from variant title (e.g., "8 sachets" -> 8)
    const quantityMatch = this.selectedVariant?.title?.match(/(\d+)/);
    const packQuantity = quantityMatch ? parseInt(quantityMatch[1]) : 1;

    return variantPrice / packQuantity;
  }

  updatePrices() {
    if (!this.selectedVariant) {
      console.warn('updatePrices called but no selected variant');
      return;
    }

    console.log('Updating prices for variant:', this.selectedVariant);
    console.log('isSubscription:', this.isSubscription);

    const variantPrice = this.selectedVariant.price;
    const comparePrice = this.selectedVariant.compare_at_price;
    // Vérifier unit_price sur la variante OU sur le produit
    const unitPrice = this.selectedVariant.unit_price || this.productData.unit_price;

    console.log('Selected variant:', this.selectedVariant);
    console.log('Variant price:', variantPrice, 'Compare price:', comparePrice, 'Unit price:', unitPrice);

    // Prix pour affichage dans les options subscription/onetime
    let displayUnitPrice, subscriptionUnitPrice;

    if (unitPrice && unitPrice > 0) {
      // Si unit_price existe, l'utiliser
      displayUnitPrice = unitPrice;
      subscriptionUnitPrice = unitPrice * (1 - this.subscriptionDiscount);
    } else {
      // Sinon, calculer le prix par sachet à partir du prix de la variante
      const pricePerUnit = this.calculatePricePerUnit(variantPrice, 1);
      displayUnitPrice = pricePerUnit;
      subscriptionUnitPrice = pricePerUnit * (1 - this.subscriptionDiscount);
    }

    // Prix TOTAL pour le bouton (toujours le prix total du pack)
    const totalPackPrice = this.isSubscription
      ? variantPrice * (1 - this.subscriptionDiscount)
      : variantPrice;

    // Format prices
    const formattedDisplayUnitPrice = this.formatPrice(displayUnitPrice);
    const formattedSubscriptionUnitPrice = this.formatPrice(subscriptionUnitPrice);
    const formattedTotalPackPrice = this.formatPrice(totalPackPrice);
    const formattedComparePrice = comparePrice ? this.formatPrice(comparePrice) : null;

    // Update price per unit in ALL pack options
    const allPackOptions = this.container.querySelectorAll('.product-form__pack-option');
    console.log('Found', allPackOptions.length, 'pack options');
    allPackOptions.forEach(packOption => {
      const radio = packOption.querySelector('input[type="radio"]');
      if (radio) {
        const variantId = parseInt(radio.value);
        const variant = this.findVariant(variantId);

        if (variant) {
          // Vérifier unit_price sur la variante OU sur le produit
          const packUnitPrice = variant.unit_price || this.productData.unit_price;

          let packDisplayPrice;
          if (packUnitPrice && packUnitPrice > 0) {
            // Si unit_price existe, l'utiliser
            packDisplayPrice = this.isSubscription
              ? packUnitPrice * (1 - this.subscriptionDiscount)
              : packUnitPrice;
          } else {
            // Sinon, calculer le prix par sachet à partir du prix de la variante
            const packVariantPrice = variant.price;
            const quantityMatch = variant.title?.match(/(\d+)/);
            const packQuantity = quantityMatch ? parseInt(quantityMatch[1]) : 1;
            const packPricePerUnit = packVariantPrice / packQuantity;
            packDisplayPrice = this.isSubscription
              ? packPricePerUnit * (1 - this.subscriptionDiscount)
              : packPricePerUnit;
          }

          const formattedPackPrice = this.formatPrice(packDisplayPrice);

          // Update the price element - suffixe unité traduisible
          const priceElement = packOption.querySelector('[data-price-per-unit]');
          if (priceElement) {
            priceElement.textContent = formattedPackPrice + this.i18n.perUnit;
          }
        }
      }
    });

    // Update subscription prices - TOUJOURS afficher "/sachet"
    const subscriptionPriceElement = this.container.querySelector('[data-subscription-price]');
    const subscriptionSuffixElement = subscriptionPriceElement?.nextElementSibling;
    if (subscriptionPriceElement) {
      subscriptionPriceElement.textContent = formattedSubscriptionUnitPrice;
      if (subscriptionSuffixElement) {
        subscriptionSuffixElement.style.display = 'inline';
      }
    }

    const onetimePriceElement = this.container.querySelector('[data-onetime-price]');
    const onetimeSuffixElement = onetimePriceElement?.nextElementSibling;
    if (onetimePriceElement) {
      onetimePriceElement.textContent = formattedDisplayUnitPrice;
      if (onetimeSuffixElement) {
        onetimeSuffixElement.style.display = 'inline';
      }
    }

    // Update total price in button
    const totalPriceElement = this.container.querySelector('[data-total-price]');
    console.log('totalPriceElement found:', !!totalPriceElement);
    if (totalPriceElement) {
      console.log('Setting total price:', formattedTotalPackPrice);
      totalPriceElement.textContent = formattedTotalPackPrice;
    }

    // Update compare price
    const comparePriceElement = this.container.querySelector('[data-compare-price]');
    if (comparePriceElement) {
      // Si la variante a un compare_at_price, toujours l'afficher en priorité
      if (comparePrice && comparePrice > variantPrice) {
        comparePriceElement.innerHTML = `<s>${formattedComparePrice}</s>`;
        comparePriceElement.style.display = 'block';
      }
      // Sinon, si subscription, afficher le prix d'origine de la variante
      else if (this.isSubscription) {
        const formattedOriginalPrice = this.formatPrice(variantPrice);
        comparePriceElement.innerHTML = `<s>${formattedOriginalPrice}</s>`;
        comparePriceElement.style.display = 'block';
      }
      // Sinon, cacher
      else {
        comparePriceElement.style.display = 'none';
      }
    }

    // Update prix au kilo (prix par sachet × 1000 / grammage sachet)
    const pricePerKgElement = this.container.querySelector('[data-price-per-kg]');
    if (pricePerKgElement) {
      const grammageG = Number(this.productData.grammage_per_sachet) || 0;
      const perSachetForKg = this.isSubscription ? subscriptionUnitPrice : displayUnitPrice;
      const pricePerKgWrapper = pricePerKgElement.closest('.product-form__price-per-kg');

      // Un pack se calcule sur le poids total de ses sachets, pas sur le
      // grammage d'un seul : sinon on divisait le prix du pack entier par 50 g
      // et on affichait 1399,80 EUR/kg pour un pack a 69,99 EUR.
      const packWeightG = Number(this.productData.pack_sachets_weight_g) || 0;
      const basePrice = packWeightG > 0 ? totalPackPrice : perSachetForKg;
      let baseWeightG = packWeightG > 0 ? packWeightG : grammageG;

      // pack_compo ne decrit la composition QU'UNE fois. Une variante qui la
      // contient N fois pese N fois plus, et c'est nombre_par_pack qui porte
      // ce N. Sans lui, le prix de la variante "36 sachets" etait divise par
      // le poids de la variante "9 sachets" : 210 EUR/kg au lieu de 52,50.
      const packMultiplier = Number(this.selectedVariant?.nombre_par_pack) || 0;
      const hasSeveralVariants = (this.productData.variants || []).length > 1;
      let weightIsKnown = true;

      if (packWeightG > 0 && hasSeveralVariants) {
        if (packMultiplier > 0) {
          baseWeightG = packWeightG * packMultiplier;
        } else {
          // Multiplicateur absent : on ne sait pas ce que pese cette variante.
          // Le prix au kilo engage le vendeur, donc on prefere ne rien
          // afficher plutot qu'un chiffre faux.
          weightIsKnown = false;
        }
      }

      if (weightIsKnown && baseWeightG > 0 && basePrice > 0) {
        const pricePerKg = basePrice * (1000 / baseWeightG);
        const template = pricePerKgElement.dataset.pricePerKgLabel || 'soit {price} le kilogramme';
        const formattedPricePerKg = this.formatPrice(pricePerKg);
        pricePerKgElement.textContent = template.includes('{price}')
          ? template.replace('{price}', formattedPricePerKg)
          : `${template} ${formattedPricePerKg}`;
        if (pricePerKgWrapper) pricePerKgWrapper.style.display = '';
      } else if (pricePerKgWrapper) {
        pricePerKgWrapper.style.display = 'none';
      }
    }
  }

  updatePackQuantity() {
    if (!this.selectedVariant) {
      console.warn('updatePackQuantity called but no selected variant');
      return;
    }

    // Get nombre_par_pack from selected variant
    const nombreParPack = this.selectedVariant.nombre_par_pack;

    // Update all pack quantity elements
    const quantityElements = this.container.querySelectorAll('[data-pack-quantity]');
    quantityElements.forEach(element => {
      if (nombreParPack) {
        element.textContent = ` x${nombreParPack}`;
      } else {
        element.textContent = '';
      }
    });
  }

  formatPrice(priceInCents) {
    const amount = (Number(priceInCents) || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'EUR';
    const locale = (window.Shopify?.locale || 'fr').replace('_', '-');
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
  }

  initAddToCart() {
    const submitButton = this.container.querySelector('[data-add-to-cart]');
    if (!submitButton) return;

    // Store original button text
    this.originalButtonText = submitButton.querySelector('p')?.textContent || this.i18n.addToCart;

    // Prevent form submission on any button click
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
    });

    // Listen to click on the specific add to cart button only
    submitButton.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      console.log('Add to cart button clicked');

      // Set loading state
      this.setButtonState(submitButton, 'loading');

      try {
        const success = await this.addToCart();

        if (success) {
          // Set success state
          this.setButtonState(submitButton, 'success');

          // Reset after 2 seconds
          setTimeout(() => {
            this.setButtonState(submitButton, 'default');
          }, 2000);
        } else {
          // Set error state
          this.setButtonState(submitButton, 'error');

          // Reset after 3 seconds
          setTimeout(() => {
            this.setButtonState(submitButton, 'default');
          }, 3000);
        }
      } catch (error) {
        // Set error state
        this.setButtonState(submitButton, 'error');

        // Reset after 3 seconds
        setTimeout(() => {
          this.setButtonState(submitButton, 'default');
        }, 3000);
      }
    });
  }

  setButtonState(button, state) {
    const buttonText = button.querySelector('p');
    if (!buttonText) return;

    switch (state) {
      case 'loading':
        button.disabled = true;
        button.style.opacity = '0.7';
        buttonText.textContent = this.i18n.adding;
        break;

      case 'success':
        button.disabled = false;
        button.style.opacity = '1';
        buttonText.textContent = this.i18n.added;
        break;

      case 'error':
        button.disabled = false;
        button.style.opacity = '1';
        buttonText.textContent = this.i18n.errorRetry;
        break;

      case 'default':
      default:
        button.disabled = false;
        button.style.opacity = '1';
        buttonText.textContent = this.originalButtonText;
        break;
    }
  }

  async addToCart() {
    // Build form data
    const formData = {
      id: this.selectedVariantId,
      quantity: 1
    };

    // Add selling plan if subscription is selected
    if (this.isSubscription) {
      const sellingPlanId = this.getSellingPlanId();
      if (sellingPlanId) {
        formData.selling_plan = sellingPlanId;
      }
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Product added to cart:', data);

        // Trigger cart update event to refresh sidecart
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Open sidecart
        this.openSidecart();

        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error adding to cart:', errorData);

        // Show elegant error notification
        if (window.notificationManager) {
          window.notificationManager.error(
            this.i18n.errorTitle,
            errorData.description || this.i18n.errorGeneric,
            6000
          );
        }

        return false;
      }
    } catch (error) {
      console.error('Error:', error);

      // Show elegant error notification
      if (window.notificationManager) {
        window.notificationManager.error(
          this.i18n.connectionErrorTitle,
          this.i18n.connectionErrorBody,
          6000
        );
      }

      return false;
    }
  }

  getSellingPlanId() {
    // Get selling plan ID directly from dropdown
    const frequencySelect = this.container.querySelector('[name="subscription_frequency"]');

    if (!frequencySelect) {
      console.warn('Subscription frequency dropdown not found');
      return null;
    }

    const sellingPlanId = parseInt(frequencySelect.value);

    if (!sellingPlanId || isNaN(sellingPlanId)) {
      console.warn('No valid selling plan ID selected:', frequencySelect.value);
      return null;
    }

    console.log('Selected selling plan ID:', sellingPlanId);
    return sellingPlanId;
  }

  openSidecart() {
    // Trigger sidecart opening
    const overlay = document.querySelector('#overlay');
    if (overlay) {
      overlay.classList.add('is-open');
      document.body.classList.add('cart-open');
    }
  }
}

// Initialize when DOM is ready
function initProductForms() {
  console.log('Initializing product forms...');
  console.log('window.productData:', window.productData);

  const productForms = document.querySelectorAll('.product-form');
  console.log('Found', productForms.length, 'product forms');

  productForms.forEach(form => {
    new ProductForm(form);
  });
}

// Check if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductForms);
} else {
  // DOM already loaded, initialize immediately
  initProductForms();
}
